/**
 * Symbolic field identifiers — the replacement for hard-coded Column-ID magic numbers
 * (1633, 1636, 1071, ...) scattered across the listeners and business-rules.
 *
 * Values are stable dotted strings, intentionally decoupled from any catalog Column id
 * or DB column name. The mapping FieldKey -> numeric Column id lives in the host
 * (`BuiltInColumnIdMap`); the mapping FieldKey -> DB field name lives in the bulkedit adapter.
 * That decoupling is the whole point: a catalog renumber can never silently break a formula.
 *
 * Source of truth for these entries: FE Plans/AUTO_CALC_COLUMN_ID_FIELDKEY_MAP.csv
 * (Phase 0.3). Entries flagged there as "confirm in Phase 2" are marked TODO below.
 */
export var FieldKey = /*#__PURE__*/function (FieldKey) {
  // ── Lease & rent ────────────────────────────────────────────────────────────
  FieldKey["LeaseStartDate"] = "lease.start-date";
  FieldKey["LeaseEndDate"] = "lease.end-date";
  FieldKey["LeaseTermMonths"] = "lease.term-months";
  FieldKey["LeaseTermUom"] = "lease.term.uom";
  FieldKey["BaseRentMonthly"] = "lease.base-rent.monthly";
  FieldKey["BaseRentMonthlyUom"] = "lease.base-rent.monthly.uom";
  FieldKey["BaseRentYearly"] = "lease.base-rent.yearly";
  FieldKey["BaseRentYearlyUom"] = "lease.base-rent.yearly.uom";
  FieldKey["EffectiveRentMonthly"] = "lease.effective-rent.monthly";
  FieldKey["EffectiveRentMonthlyUom"] = "lease.effective-rent.monthly.uom";
  FieldKey["EffectiveRentYearly"] = "lease.effective-rent.yearly";
  FieldKey["EffectiveRentYearlyUom"] = "lease.effective-rent.yearly.uom";
  FieldKey["AskingRentMonthly"] = "lease.asking-rent.monthly";
  FieldKey["AskingRentMonthlyUom"] = "lease.asking-rent.monthly.uom";
  FieldKey["AskingRentYearly"] = "lease.asking-rent.yearly";
  FieldKey["AskingRentYearlyUom"] = "lease.asking-rent.yearly.uom";
  FieldKey["AreaLeased"] = "lease.area-leased";
  FieldKey["AreaLeasedUom"] = "lease.area-leased.uom";
  FieldKey["LeaseArrangementTotalValue"] = "lease.arrangement-total-value";
  FieldKey["LeaseArrangementTotalValueUom"] = "lease.arrangement-total-value.uom";
  FieldKey["RentPerSqm"] = "lease.rent-per-sqm";
  FieldKey["RentPerSqmUom"] = "lease.rent-per-sqm.uom";
  // ── Availability rates ──────────────────────────────────────────────────────
  FieldKey["AvailMinAskingRateMonthly"] = "availability.min-asking-rate.monthly";
  FieldKey["AvailMinAskingRateMonthlyUom"] = "availability.min-asking-rate.monthly.uom";
  FieldKey["AvailMaxAskingRateMonthly"] = "availability.max-asking-rate.monthly";
  FieldKey["AvailMaxAskingRateMonthlyUom"] = "availability.max-asking-rate.monthly.uom";
  FieldKey["AvailMinAskingRateYearly"] = "availability.min-asking-rate.yearly";
  FieldKey["AvailMinAskingRateYearlyUom"] = "availability.min-asking-rate.yearly.uom";
  FieldKey["AvailMaxAskingRateYearly"] = "availability.max-asking-rate.yearly";
  FieldKey["AvailMaxAskingRateYearlyUom"] = "availability.max-asking-rate.yearly.uom";
  FieldKey["AvailAskingRent"] = "availability.asking-rent";
  FieldKey["AvailAskingRentYearlyTotal"] = "availability.asking-rent.yearly-total";
  FieldKey["AvailAskingRentalRateMonthly"] = "availability.asking-rental-rate.monthly";
  FieldKey["AvailTotalRentEurosPerMonth"] = "availability.total-rent-eur.monthly";
  FieldKey["AvailSpaceSize"] = "availability.space-size";
  FieldKey["AvailOpex"] = "availability.opex";
  FieldKey["AvailAskingRateType"] = "availability.asking-rate-type";
  FieldKey["AvailCamChargesPerArea"] = "availability.cam-charges-per-area";
  FieldKey["AvailCamCharges"] = "availability.cam-charges";
  FieldKey["AvailServiceChargeYearly"] = "availability.service-charge.yearly";
  FieldKey["AvailMinDivisibleArea"] = "availability.min-divisible-area";
  // Asking-rent 4-way UOM group + service-charge 3-way UOM group (T2.7, design doc §3;
  // control names from availability-rates-listeners.model.ts AskingRent/ServiceCharge enums,
  // group wiring in availability-uom-listener.ts).
  FieldKey["AvailAskingRentUom"] = "availability.asking-rent.uom";
  FieldKey["AvailAskingRentYearlyTotalUom"] = "availability.asking-rent.yearly-total.uom";
  FieldKey["AvailAskingRentalRateMonthlyUom"] = "availability.asking-rental-rate.monthly.uom";
  FieldKey["AvailTotalRentEurosPerMonthUom"] = "availability.total-rent-eur.monthly.uom";
  FieldKey["AvailServiceChargeUom"] = "availability.cam-charges-per-area.uom";
  FieldKey["AvailServiceChargePerMonthUom"] = "availability.cam-charges.uom";
  FieldKey["AvailServiceChargePerYearUom"] = "availability.service-charge.yearly.uom";
  // ── Property totals & rates ─────────────────────────────────────────────────
  FieldKey["DirectAvailableSpace"] = "property.direct-available-space";
  FieldKey["DirectAvailableSpaceUom"] = "property.direct-available-space.uom";
  FieldKey["SubleaseAvailableSpace"] = "property.sublease-available-space";
  FieldKey["SubleaseAvailableSpaceUom"] = "property.sublease-available-space.uom";
  FieldKey["DirectVacantSpace"] = "property.direct-vacant-space";
  FieldKey["DirectVacantSpaceUom"] = "property.direct-vacant-space.uom";
  FieldKey["SubleaseVacantSpace"] = "property.sublease-vacant-space";
  FieldKey["SubleaseVacantSpaceUom"] = "property.sublease-vacant-space.uom";
  FieldKey["DirectNotVacantSpace"] = "property.direct-not-vacant-space";
  FieldKey["DirectNotVacantSpaceUom"] = "property.direct-not-vacant-space.uom";
  FieldKey["SubleaseNotVacantSpace"] = "property.sublease-not-vacant-space";
  FieldKey["SubleaseNotVacantSpaceUom"] = "property.sublease-not-vacant-space.uom";
  // Totals (direct + sublease). Column ids are a host BuiltInColumnIdMap concern (TODO sheet gap).
  FieldKey["TotalAvailableSpace"] = "property.total-available-space";
  FieldKey["TotalVacantSpace"] = "property.total-vacant-space";
  FieldKey["TotalNotVacantSpace"] = "property.total-not-vacant-space";
  // Total UOMs — written by the UOM-aware total rules (T2.6); catalog col ids from
  // columns-dictionary.ts (value cols: total available 1075, total vacant 1081, total not-vacant 16380).
  FieldKey["TotalAvailableSpaceUom"] = "property.total-available-space.uom";
  FieldKey["TotalVacantSpaceUom"] = "property.total-vacant-space.uom";
  FieldKey["TotalNotVacantSpaceUom"] = "property.total-not-vacant-space.uom";
  // Vacant-available triple (FE controls *_vacant_available_area; 4th binder group, T2.6)
  FieldKey["DirectVacantAvailableSpace"] = "property.direct-vacant-available-space";
  FieldKey["DirectVacantAvailableSpaceUom"] = "property.direct-vacant-available-space.uom";
  FieldKey["SubleaseVacantAvailableSpace"] = "property.sublease-vacant-available-space";
  FieldKey["SubleaseVacantAvailableSpaceUom"] = "property.sublease-vacant-available-space.uom";
  FieldKey["TotalVacantAvailableSpace"] = "property.total-vacant-available-space";
  FieldKey["TotalVacantAvailableSpaceUom"] = "property.total-vacant-available-space.uom";
  FieldKey["PropMinDirectAskingRateMonthly"] = "property.min-direct-asking-rate.monthly";
  FieldKey["PropMaxDirectAskingRateMonthly"] = "property.max-direct-asking-rate.monthly";
  FieldKey["PropMinDirectAskingRateYearly"] = "property.min-direct-asking-rate.yearly";
  FieldKey["PropMaxDirectAskingRateYearly"] = "property.max-direct-asking-rate.yearly";
  FieldKey["PropMinSubleaseAskingRateMonthly"] = "property.min-sublease-asking-rate.monthly";
  FieldKey["PropMaxSubleaseAskingRateMonthly"] = "property.max-sublease-asking-rate.monthly";
  FieldKey["PropMinSubleaseAskingRateYearly"] = "property.min-sublease-asking-rate.yearly";
  FieldKey["PropMaxSubleaseAskingRateYearly"] = "property.max-sublease-asking-rate.yearly";
  // Min/max rate UOMs (T2.7, design doc §3 — F3 pairs in property/uom-listener.ts;
  // control names from rates-listeners.model.ts MinMaxRateMonthlyYearly).
  FieldKey["PropMinDirectAskingRateMonthlyUom"] = "property.min-direct-asking-rate.monthly.uom";
  FieldKey["PropMaxDirectAskingRateMonthlyUom"] = "property.max-direct-asking-rate.monthly.uom";
  FieldKey["PropMinDirectAskingRateYearlyUom"] = "property.min-direct-asking-rate.yearly.uom";
  FieldKey["PropMaxDirectAskingRateYearlyUom"] = "property.max-direct-asking-rate.yearly.uom";
  FieldKey["PropMinSubleaseAskingRateMonthlyUom"] = "property.min-sublease-asking-rate.monthly.uom";
  FieldKey["PropMaxSubleaseAskingRateMonthlyUom"] = "property.max-sublease-asking-rate.monthly.uom";
  FieldKey["PropMinSubleaseAskingRateYearlyUom"] = "property.min-sublease-asking-rate.yearly.uom";
  FieldKey["PropMaxSubleaseAskingRateYearlyUom"] = "property.max-sublease-asking-rate.yearly.uom";
  FieldKey["PropAvgDirectAskingRateMonthly"] = "property.avg-direct-asking-rate.monthly";
  FieldKey["PropAvgDirectAskingRateMonthlyUom"] = "property.avg-direct-asking-rate.monthly.uom";
  FieldKey["PropAvgDirectAskingRateYearly"] = "property.avg-direct-asking-rate.yearly";
  FieldKey["PropAvgDirectAskingRateYearlyUom"] = "property.avg-direct-asking-rate.yearly.uom";
  FieldKey["PropAvgSubleaseAskingRateMonthly"] = "property.avg-sublease-asking-rate.monthly";
  FieldKey["PropAvgSubleaseAskingRateMonthlyUom"] = "property.avg-sublease-asking-rate.monthly.uom";
  FieldKey["PropAvgSubleaseAskingRateYearly"] = "property.avg-sublease-asking-rate.yearly";
  FieldKey["PropAvgSubleaseAskingRateYearlyUom"] = "property.avg-sublease-asking-rate.yearly.uom";
  // ── Sale & portfolio-sale ───────────────────────────────────────────────────
  FieldKey["SaleGrossArea"] = "sale.gross-area";
  FieldKey["SaleGrossAreaUom"] = "sale.gross-area.uom";
  FieldKey["QuotingPrice"] = "sale.quoting-price";
  FieldKey["QuotingPriceUom"] = "sale.quoting-price.uom";
  FieldKey["SalePrice"] = "sale.price";
  FieldKey["SalePriceUom"] = "sale.price.uom";
  FieldKey["PricePerAcre"] = "sale.price-per-acre";
  FieldKey["PricePerSqFt"] = "sale.price-per-sqft";
  FieldKey["PricePerSqFtUom"] = "sale.price-per-sqft.uom";
  FieldKey["CurrentRentPerAnnum"] = "sale.current-rent-per-annum";
  FieldKey["CurrentRentPerAnnumUom"] = "sale.current-rent-per-annum.uom";
  FieldKey["SalePricePerArea"] = "sale.price-per-area";
  FieldKey["SalePricePerAreaUom"] = "sale.price-per-area.uom";
  FieldKey["CurrentRentPerAnnumPerArea"] = "sale.current-rent-per-annum-per-area";
  FieldKey["CurrentRentPerAnnumPerAreaUom"] = "sale.current-rent-per-annum-per-area.uom";
  FieldKey["BuyerCost"] = "sale.buyer-cost";
  FieldKey["BuyerCostUom"] = "sale.buyer-cost.uom";
  FieldKey["VacantEstimatedRentalAmount"] = "sale.vacant-estimated-rental-amount";
  FieldKey["VacantEstimatedRentalAmountUom"] = "sale.vacant-estimated-rental-amount.uom";
  FieldKey["PotentialGrossIncome"] = "sale.potential-gross-income";
  FieldKey["PotentialGrossIncomeUom"] = "sale.potential-gross-income.uom";
  FieldKey["GrossInvestmentAmount"] = "sale.gross-investment-amount";
  FieldKey["GrossInvestmentAmountUom"] = "sale.gross-investment-amount.uom";
  FieldKey["VacantPossessionValueAmount"] = "sale.vacant-possession-value.amount";
  FieldKey["VacantPossessionValueAmountUom"] = "sale.vacant-possession-value.amount.uom";
  FieldKey["VacantPossessionValueRate"] = "sale.vacant-possession-value.rate";
  FieldKey["VacantPossessionValueRateUom"] = "sale.vacant-possession-value.rate.uom";
  FieldKey["SalePriceActual"] = "sale.price-actual";
  FieldKey["SalePriceActualUom"] = "sale.price-actual.uom";
  FieldKey["NetRentableArea"] = "sale.net-rentable-area";
  FieldKey["PassingRentAmountYearly"] = "sale.passing-rent-amount-yearly";
  FieldKey["PassingRentAmountYearlyUom"] = "sale.passing-rent-amount-yearly.uom";
  FieldKey["PortfolioSalesPrice"] = "sale.portfolio.price";
  FieldKey["PortfolioNra"] = "sale.portfolio.nra";
  FieldKey["PortfolioPricePerSqFt"] = "sale.portfolio.price-per-sqft";
  FieldKey["NetRentableAreaUom"] = "sale.net-rentable-area.uom";
  FieldKey["SaleOperatingExpenses"] = "sale.operating-expenses";
  FieldKey["SaleYieldType"] = "sale.yield.type";
  FieldKey["SaleYieldPercent"] = "sale.yield.percent";
  FieldKey["SaleYieldMultiplier"] = "sale.yield.multiplier";
  FieldKey["SalePgiPerArea"] = "sale.pgi-per-area";
  FieldKey["SalePgiPerAreaUom"] = "sale.pgi-per-area.uom";
  // ── ITZA (object-shaped today; no flat Column id — confirm field paths in Phase 2) ──
  FieldKey["ItzaPercent"] = "itza.percent";
  FieldKey["ItzaArea"] = "itza.area";
  FieldKey["FloorSuiteSize"] = "itza.floor-suite-size";
  FieldKey["AskingRentPerItza"] = "itza.asking-rent-per-itza";
  // ── Space terms (Column ids resolved by host BuiltInColumnIdMap; see space-terms-calc-helper.ts) ──
  FieldKey["SpaceAchievableRent"] = "space.achievable-rent";
  FieldKey["SpaceRentFree"] = "space.rent-free";
  FieldKey["SpaceLeaseTerm"] = "space.lease-term";
  FieldKey["SpaceEffectiveRent"] = "space.effective-rent";
  FieldKey["SpaceAskingRentAmount"] = "space.asking-rent-amount";
  FieldKey["SpaceTotalMonthlyOutgoing"] = "space.total-monthly-outgoing";
  FieldKey["SpaceAskingRentalRate"] = "space.asking-rental-rate";
  FieldKey["SpaceAchievableRentAmount"] = "space.achievable-rent-amount";
  FieldKey["SpaceAvailableSpace"] = "space.available-space";
  FieldKey["SpaceAreaMeasurementType"] = "space.area-measurement-type";
  FieldKey["SpaceUtilizationEfficiencyRate"] = "space.utilization-efficiency-rate";
  FieldKey["SpaceNetRentableArea"] = "space.net-rentable-area";
  // HK-Retail bidirectional size pairs (auto-calculation-space-hk-retail.ts); columns are HK-catalog-specific
  FieldKey["SpaceMgmtFeePerMonth"] = "space.mgmt-fee-per-month";
  FieldKey["SpaceMgmtFeePerSqFt"] = "space.mgmt-fee-per-sqft";
  FieldKey["SpaceTotalMonthlyAskingRent"] = "space.total-monthly-asking-rent";
  FieldKey["SpaceQuotedUnitRent"] = "space.quoted-unit-rent";
  FieldKey["SpaceSalePrice"] = "space.sale-price";
  FieldKey["SpaceSalePricePerSqFt"] = "space.sale-price-per-sqft";
  FieldKey["SpaceAcNormalCharges"] = "space.ac-normal-charges";
  FieldKey["SpaceAcChargesPerSqFt"] = "space.ac-charges-per-sqft";
  FieldKey["SpaceRentalMonthly"] = "space.rental-monthly";
  FieldKey["SpaceRentalPerSqFt"] = "space.rental-per-sqft";
  return FieldKey;
}(FieldKey || {});

